/**
 * Centralized Authentication Service
 * Replaces direct localStorage access with secure, centralized auth management
 * 
 * This service provides:
 * - Secure token management
 * - User session handling
 * - Permission checking
 * - Automatic token refresh
 * - Security event logging
 */

import { STORAGE_KEYS, USER_ROLES, PERMISSIONS } from '../constants/appConfig';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

class AuthService {
  constructor() {
    this.listeners = new Set();
    this.refreshTimer = null;
    this.isRefreshing = false;
    
    // Initialize auth state from storage
    this.initializeAuth();
    
    // Set up automatic token refresh
    this.setupTokenRefresh();
    
    // Listen for storage changes (multi-tab support)
    this.setupStorageListener();
  }

  /**
   * Initialize authentication state from localStorage
   * BRIDGE: Support both new and legacy localStorage keys
   */
  initializeAuth() {
    try {
      const token = this.getStoredToken();
      const userData = this.getStoredUserData();

      // If new system has data, use it
      if (token && userData) {
        this.validateSession();
        return;
      }

      // BRIDGE: Check for legacy localStorage data and migrate it
      const legacyUserEmail = localStorage.getItem("user");
      const legacyRoleId = localStorage.getItem("roleId");
      const legacyCorpId = localStorage.getItem("corpId");
      const legacyAdminName = localStorage.getItem("adminName");
      const legacyLogin = localStorage.getItem("login");

      if (legacyUserEmail && legacyRoleId && legacyLogin === "true") {
        console.log('🔄 Migrating legacy auth data to new AuthService...');

        // Create user data object from legacy localStorage
        const migratedUserData = {
          emailID: legacyUserEmail,
          roleID: legacyRoleId,
          corporateID: legacyCorpId || "",
          name: legacyAdminName || "",
          firstName: legacyAdminName || "",
          departmentID: localStorage.getItem("adminDepartmentID") || "",
          corporateAddress: localStorage.getItem("corporateAddress") || "",
          userType: localStorage.getItem("userType") || "",
          corporateName: localStorage.getItem("cpName") || ""
        };

        // Store in new format (without token for now since backend doesn't provide it)
        this.setStoredUserData(migratedUserData);
        // Use a placeholder token to indicate authenticated state
        this.setStoredToken("legacy-session-token");

        console.log(' Legacy auth data migrated successfully');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.clearAuth();
    }
  }

  /**
   * Login user with credentials
   */
  async login(credentials) {
    try {
      const response = await fetch(`/api${API_ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      
      if (data.success) {
        await this.setAuthData(data);
        this.notifyListeners('login', data.user);
        return { success: true, user: data.user };
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user and clear all auth data
   */
  async logout() {
    try {
      const token = this.getToken();
      
      if (token) {
        // Call logout endpoint to invalidate token on server
        await fetch(`/api${API_ENDPOINTS.AUTH.LOGOUT}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    } finally {
      this.clearAuth();
      this.notifyListeners('logout');
    }
  }

  /**
   * Set authentication data securely
   */
  async setAuthData(authData) {
    const { token, user, refreshToken, expiresIn } = authData;
    
    // Calculate expiration time
    const expirationTime = Date.now() + (expiresIn * 1000);
    
    // Store auth data
    this.setStoredToken(token);
    this.setStoredUserData(user);
    this.setStoredRefreshToken(refreshToken);
    this.setStoredExpiration(expirationTime);
    
    // Set up token refresh timer
    this.scheduleTokenRefresh(expiresIn);
  }

  /**
   * Clear all authentication data
   */
  clearAuth() {
    // Clear stored data
    this.removeStoredToken();
    this.removeStoredUserData();
    this.removeStoredRefreshToken();
    this.removeStoredExpiration();
    
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Get current authentication token
   */
  getToken() {
    const token = this.getStoredToken();
    
    if (!token) return null;
    
    // Check if token is expired
    if (this.isTokenExpired()) {
      this.clearAuth();
      return null;
    }
    
    return token;
  }

  /**
   * Get current user data
   * BRIDGE: Support both new userData and legacy localStorage
   */
  getUser() {
    const userData = this.getStoredUserData();

    // If we have new format user data, use it
    if (userData && !this.isTokenExpired()) {
      return userData;
    }

    // BRIDGE: Fall back to legacy localStorage data
    const legacyLogin = localStorage.getItem("login");
    if (legacyLogin === "true") {
      return {
        emailID: localStorage.getItem("user"),
        roleID: localStorage.getItem("roleId"),
        corporateID: localStorage.getItem("corpId") || "",
        name: localStorage.getItem("adminName") || "",
        firstName: localStorage.getItem("adminName") || "",
        departmentID: localStorage.getItem("adminDepartmentID") || "",
        corporateAddress: localStorage.getItem("corporateAddress") || "",
        userType: localStorage.getItem("userType") || "",
        corporateName: localStorage.getItem("cpName") || ""
      };
    }

    return null;
  }

  /**
   * Check if user is authenticated
   * BRIDGE: Support both new tokens and legacy localStorage
   */
  isAuthenticated() {
    const token = this.getToken();
    if (token) return true;

    // BRIDGE: Check legacy localStorage for authentication
    const legacyLogin = localStorage.getItem("login");
    const legacyUserEmail = localStorage.getItem("user");

    return legacyLogin === "true" && !!legacyUserEmail;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission) {
    const user = this.getUser();
    
    if (!user || !user.roleID) {
      return false;
    }
    
    const allowedRoles = PERMISSIONS[permission];
    
    if (!allowedRoles) {
      console.warn(`Unknown permission: ${permission}`);
      return false;
    }
    
    return allowedRoles.includes(user.roleID);
  }

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    const user = this.getUser();
    return user?.roleID === role;
  }

  /**
   * Get user's corporate ID
   */
  getCorporateId() {
    const user = this.getUser();
    return user?.corporateID || null;
  }

  /**
   * Get user's email
   */
  getUserEmail() {
    const user = this.getUser();
    return user?.emailID || null;
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    if (this.isRefreshing) {
      return; // Prevent multiple simultaneous refresh attempts
    }
    
    this.isRefreshing = true;
    
    try {
      const refreshToken = this.getStoredRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // If refresh endpoint isn't defined, skip refresh gracefully
      const refreshPath = API_ENDPOINTS?.AUTH?.REFRESH;
      if (!refreshPath) {
        console.warn('Auth refresh endpoint not configured; skipping token refresh');
        return false;
      }
      
      const response = await fetch(`/api${refreshPath}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
      
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Token refresh returned non-JSON response');
      }
      const data = await response.json();
      
      if (data.success) {
        await this.setAuthData(data);
        this.notifyListeners('tokenRefresh', data.user);
        return true;
      } else {
        throw new Error(data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuth();
      this.notifyListeners('authError', error);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Validate current session
   */
  async validateSession() {
    try {
      const token = this.getToken();
      
      if (!token) {
        return false;
      }

      // If validate endpoint isn't configured, consider token-based session as valid
      const validatePath = API_ENDPOINTS?.AUTH?.VALIDATE;
      if (!validatePath) {
        // No server-side validation route configured; rely on token expiry
        return true;
      }

      const response = await fetch(`/api${validatePath}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          console.warn('Session validate returned non-JSON response');
          return false;
        }
        const data = await response.json();
        if (data.valid) {
          return true;
        }
      }
      
      // Session is invalid, clear auth
      this.clearAuth();
      return false;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  }

  /**
   * Add authentication state listener
   */
  addListener(callback) {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of auth state changes
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Auth listener error:', error);
      }
    });
  }

  // Private methods for secure storage operations
  getStoredToken() {
    return localStorage.getItem(STORAGE_KEYS.USER_TOKEN);
  }

  setStoredToken(token) {
    localStorage.setItem(STORAGE_KEYS.USER_TOKEN, token);
  }

  removeStoredToken() {
    localStorage.removeItem(STORAGE_KEYS.USER_TOKEN);
  }

  getStoredUserData() {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  setStoredUserData(userData) {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }

  removeStoredUserData() {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  getStoredRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  setStoredRefreshToken(token) {
    localStorage.setItem('refreshToken', token);
  }

  removeStoredRefreshToken() {
    localStorage.removeItem('refreshToken');
  }

  getStoredExpiration() {
    const expiration = localStorage.getItem('tokenExpiration');
    return expiration ? parseInt(expiration, 10) : null;
  }

  setStoredExpiration(expiration) {
    localStorage.setItem('tokenExpiration', expiration.toString());
  }

  removeStoredExpiration() {
    localStorage.removeItem('tokenExpiration');
  }

  isTokenExpired() {
    const expiration = this.getStoredExpiration();

    if (!expiration) {
      // BRIDGE: For legacy tokens, check if login is still valid
      const token = this.getStoredToken();
      if (token === "legacy-session-token") {
        return localStorage.getItem("login") !== "true";
      }
      return false; // No expiration set, assume valid for new tokens
    }

    return Date.now() > expiration;
  }

  scheduleTokenRefresh(expiresIn) {
    // Refresh token 5 minutes before expiration
    const refreshTime = (expiresIn - 300) * 1000;
    
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    }
  }

  setupTokenRefresh() {
    // Check for token refresh on app focus
    window.addEventListener('focus', () => {
      if (this.isAuthenticated() && this.isTokenExpired()) {
        this.refreshToken();
      }
    });
  }

  setupStorageListener() {
    // Listen for storage changes (multi-tab support)
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEYS.USER_TOKEN) {
        if (!event.newValue) {
          // Token was removed in another tab
          this.clearAuth();
          this.notifyListeners('logout');
        }
      }
    });
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

// Export convenience functions
export const {
  login,
  logout,
  getToken,
  getUser,
  isAuthenticated,
  hasPermission,
  hasRole,
  getCorporateId,
  getUserEmail,
  addListener
} = authService;
